import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import DoneOutlineOutlinedIcon from '@mui/icons-material/DoneOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useTranslation } from 'react-i18next';
import useTpvsService from '../../../services/TpvsService';
import SttFullPageSpinner from '../../../sporttia/spinners/SttFullPageSpinner';
import FullPageError from '../../../components/error/FullPageError';
import { AppContext } from '../../../sporttia/AppContext';
import { isObject } from '../../../types/common';
import { AlertDialog } from '../../../components/dialogs/AlertDialog';
import type { Tpv } from '../../../types/models/Tpv';

/**
 * Sporttia ofrece diferentes métodos de pago. Este componente renderiza el componente para pagar según el banco del TPV.
 * Anteriormente, se utilizaba TPVSwitcherLegacy con este propósito. Dado que no se han migrado todos los métodos de pago al nuevo componente,
 * TPVSwitcherLegacy se utiliza como fallback.
 */
export default function TPVUrlPurchase({ tpv }: { tpv: Tpv }) {
    const tpvService = useTpvsService();
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        mutate: purchasePayment,
        data,
        status,
    } = tpvService.useCreateTpvPurchase({
        onError: (error: any) => {
            if (!isObject(error) || !(typeof error.description === 'string')) {
                return;
            }
            // ¿Es necesario traducir `error.description`?
            cxt.showMessage('E', t(error.description), 3000);
        },
    });

    useEffect(() => {
        purchasePayment({
            id: tpv.id,
        });
    }, [tpv, purchasePayment]);

    /**
     * Returns the color of the background according to the type of POS to paint the background of the meter
     * and make it appear embedded in the virtual POS.
     * @returns {string} String color.
     */
    const getBackgroundColorByTpvConf = () => {
        switch (tpv.tpvConf?.bank) {
            case 'REDSYS_HMAC256':
                return '#F8F8F8';
            case 'PAYCOMET':
            case 'CCM':
            default:
                return 'white';
        }
    };

    if (status === 'error') {
        return <FullPageError />;
    }

    if (status === 'pending') {
        return <SttFullPageSpinner />;
    }

    if (!data) {
        return null;
    }

    const { urlRedirection } = data;

    return (
        <Box
            display="flex"
            flexDirection="column"
            style={{
                height: '100vh',
                backgroundColor: getBackgroundColorByTpvConf(),
            }}
        >
            {urlRedirection && (
                <AlertDialog
                    title={t('TermsAndConditions')}
                    open
                    buttons={[
                        {
                            icon: <CloseOutlinedIcon />,
                            caption: 'Denegar',
                            onClick: () => {
                                navigate('/');
                            },
                        },
                        {
                            icon: <DoneOutlineOutlinedIcon />,
                            caption: 'Aceptar',
                            onClick: () => {
                                window.location.replace(urlRedirection);
                            },
                        },
                    ]}
                    subtitle={
                        <>
                            {t('IHaveReadAndAccept')}{' '}
                            <Link
                                onClick={() => {
                                    window
                                        .open(
                                            'https://www.sporttia.com/es/condiciones-generales-de-contratacion/',
                                            '_blank',
                                        )
                                        ?.focus();
                                }}
                            >
                                {t('TermsAndConditions')}
                            </Link>
                        </>
                    }
                />
            )}
        </Box>
    );
}
